import React, { useEffect, useState, useRef, useCallback } from "react";
import { useTutorial } from "../../context/TutorialContext";
import { db } from "../../firebase";
import { collection, query, where, onSnapshot } from "firebase/firestore";
import { useBuilding } from "../../context/BuildingContext";
import { useUser } from "../../context/UserContext";
import Loader from "../../components/Loader";
import { GoogleMap, useJsApiLoader, Marker, InfoWindow } from "@react-google-maps/api";
import {
  FaBuilding,
  FaMapMarkerAlt,
  FaPlusCircle,
  FaMap,
  FaList,
  FaMapPin,
} from "react-icons/fa";
import AdminModal from "../../components/AdminModal";
import FormField from "../../components/FormField";
import ScreenHeader from "../../components/ScreenHeader";
import { useToast } from "../../components/Toast";
import StatusBadge from "../../components/StatusBadge";
import {
  createBuilding,
  refreshMyAccess,
  nuevoRequestId,
  mensajeDeError,
  sinMensajeDelServidor,
  codigoDeError,
} from "../../utils/identityApi";
import {
  colors,
  shadows,
  radius,
  spacing,
  typography,
  cardBase,
  btnPrimary,
  btnSecondary,
  btnGhost,
  inputBase,
  pageTitle,
} from "../../utils/designTokens";

const GOOGLE_MAPS_API_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY || "";
const libraries = ["places"];

// Mismas opciones que valida createBuilding (functions/identity/edificios.js).
const DEPARTAMENTOS_UY = ["Artigas", "Canelones", "Cerro Largo", "Colonia", "Durazno", "Flores", "Florida", "Lavalleja", "Maldonado", "Montevideo", "Paysandú", "Río Negro", "Rivera", "Rocha", "Salto", "San José", "Soriano", "Tacuarembó", "Treinta y Tres"];

/** Sin tildes ni mayúsculas, como compara el servidor ("Paysandu" = "Paysandú"). */
const plano = (s) => String(s || "").normalize("NFD").replace(/[\u0300-\u036f]/g, "").trim().toLowerCase();

const FORM_INICIAL = {
  name: "",
  address: "",
  city: "",
  department: "",
  country: "Uruguay",
  padron: "",
  floors: "",
  elevators: "",
  complex: false,
  lat: null,
  lng: null,
};

// Pisos y ascensores: enteros sin signo (el servidor rechaza decimales y negativos).
const ENTERO = /^\d+$/;
const MAX_PISOS = 300;
const MAX_ASCENSORES = 100;

// Errores genéricos del SDK de Functions (red caída, timeout): su message es el
// código ("internal", "deadline-exceeded"), que no le dice nada al usuario. El
// requestId se conserva, así que reintentar no crea el edificio dos veces.
const MENSAJE_GENERICO = {
  "deadline-exceeded": "El servidor tardó demasiado en responder. Reintentá: el edificio no se va a crear dos veces.",
  internal: "No pudimos confirmar el alta (error de conexión o del servidor). Reintentá: el edificio no se va a crear dos veces.",
  unavailable: "El servidor no está disponible. Reintentá en unos segundos.",
};

const mensajeLegible = (e, fallback) =>
  sinMensajeDelServidor(e) ? MENSAJE_GENERICO[codigoDeError(e)] || fallback : mensajeDeError(e, fallback);

// Solicitudes de baja sin resolver. approveBuildingDeletion (servidor) suma
// 'procesando' y 'error' a 'pendiente': el edificio sigue en la lista hasta que
// la baja termina.
const ESTADOS_BAJA_ABIERTA = ["pendiente", "procesando", "error"];
const PRIORIDAD_BAJA = { pendiente: 1, error: 2, procesando: 3 };
// Firestore: hasta 30 disyunciones por consulta. buildingDocId in (10) × status
// in (3) = 30.
const DOCS_POR_CONSULTA_BAJA = Math.floor(30 / ESTADOS_BAJA_ABIERTA.length);
const BADGE_BAJA = {
  pendiente: { status: "pendiente", label: "Pendiente de eliminación" },
  procesando: { status: "en_proceso", label: "Eliminación en curso" },
  error: { status: "error", label: "Eliminación con error" },
};

const mapContainerStyle = {
  width: "100%",
  height: "500px",
  borderRadius: radius.md,
};

const defaultCenter = {
  lat: -34.9011, // Montevideo
  lng: -56.1645,
};

const MisEdificios = ({ setSelected }) => {
  const { buildings, loading, setSelectedBuilding, refreshBuildings } = useBuilding();
  const { showToast } = useToast();
  const [showModal, setShowModal] = useState(false);
  const [saving, setSaving] = useState(false);
  const [viewMode, setViewMode] = useState("list"); // "list" | "map"
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedMarker, setSelectedMarker] = useState(null);
  // buildingDocId → status de su solicitud de baja abierta (ESTADOS_BAJA_ABIERTA).
  const [pendingDeletions, setPendingDeletions] = useState(() => new Map());

  const [formData, setFormData] = useState(FORM_INICIAL);
  const [formErrors, setFormErrors] = useState({});
  // Clave de idempotencia del alta: se reusa mientras el pedido sea el mismo
  // (reintento por timeout, red o doble click) y se renueva si cambia algún
  // dato. Así un alta que quedó sin respuesta no se confunde con la de otro
  // edificio cargado después.
  const intentoRef = useRef({ firma: null, requestId: null });

  // Registro de acciones para el tutorial guiado
  const { registerAction, unregisterAction } = useTutorial();
  useEffect(() => {
    registerAction("edificios-abrir-modal", () => setShowModal(true));
    registerAction("edificios-cerrar-modal", () => { setShowModal(false); setFormErrors({}); });
    return () => {
      unregisterAction("edificios-abrir-modal");
      unregisterAction("edificios-cerrar-modal");
    };
  }, [registerAction, unregisterAction]);

  // Google Maps
  const { isLoaded, loadError } = useJsApiLoader({
    googleMapsApiKey: GOOGLE_MAPS_API_KEY,
    libraries,
  });

  const autocompleteRef = useRef(null);
  const inputRef = useRef(null);
  const mapRef = useRef(null);

  const onMapLoad = useCallback((map) => {
    mapRef.current = map;
  }, []);

  // Listener en tiempo real para solicitudes de eliminación sin resolver. El
  // superadmin las ve todas; un admin solo puede consultar las de SUS edificios
  // (la regla se prueba con buildingDocId fijado en la consulta), en tandas de
  // 10 edificios por listener.
  const { userType } = useUser();
  const esSuperadmin = Number(userType) === 2;
  const docIdsEdificios = esSuperadmin
    ? ""
    : [...new Set(buildings.map((b) => b.id).filter(Boolean))].sort().join("|");
  useEffect(() => {
    const col = collection(db, "building_deletion_requests");
    let consultas;
    if (esSuperadmin) {
      consultas = [query(col, where("status", "in", ESTADOS_BAJA_ABIERTA))];
    } else {
      const ids = docIdsEdificios ? docIdsEdificios.split("|") : [];
      consultas = [];
      for (let i = 0; i < ids.length; i += DOCS_POR_CONSULTA_BAJA) {
        consultas.push(
          query(
            col,
            where("buildingDocId", "in", ids.slice(i, i + DOCS_POR_CONSULTA_BAJA)),
            where("status", "in", ESTADOS_BAJA_ABIERTA)
          )
        );
      }
    }
    if (consultas.length === 0) {
      setPendingDeletions(new Map());
      return undefined;
    }
    const partes = consultas.map(() => []);
    const publicar = () => {
      const porEdificio = new Map();
      partes.flat().forEach(({ buildingDocId, status }) => {
        if (!buildingDocId) return;
        // Con más de una abierta para el mismo edificio, manda la más avanzada.
        const previo = porEdificio.get(buildingDocId);
        if (!previo || (PRIORIDAD_BAJA[status] || 0) > (PRIORIDAD_BAJA[previo] || 0)) {
          porEdificio.set(buildingDocId, status);
        }
      });
      setPendingDeletions(porEdificio);
    };
    const unsubs = consultas.map((q, i) =>
      onSnapshot(
        q,
        (snap) => {
          partes[i] = snap.docs.map((d) => d.data());
          publicar();
        },
        (err) => console.error("Error escuchando solicitudes de eliminación:", err)
      )
    );
    return () => unsubs.forEach((u) => u());
  }, [esSuperadmin, docIdsEdificios]);

  // Inicializar autocomplete cuando se abre el modal
  useEffect(() => {
    if (showModal && isLoaded && inputRef.current && !autocompleteRef.current) {
      const autocomplete = new window.google.maps.places.Autocomplete(inputRef.current, {
        types: ["address"],
        componentRestrictions: { country: ["uy"] },
      });

      autocomplete.addListener("place_changed", () => {
        const place = autocomplete.getPlace();
        if (place.geometry) {
          const lat = place.geometry.location.lat();
          const lng = place.geometry.location.lng();
          
          // Extraer componentes de la dirección
          let city = "";
          let department = "";
          let country = "";
          let streetAddress = "";

          place.address_components?.forEach((component) => {
            if (component.types.includes("locality")) {
              city = component.long_name;
            }
            if (component.types.includes("administrative_area_level_1")) {
              // Normalizar nombre del departamento desde Google Places
              // Intentar con short_name primero, luego long_name limpio
              const candidates = [
                component.short_name,
                component.long_name,
                component.long_name.replace(/^Departamento de\s+/i, "").replace(/\s+Department$/i, "").trim(),
              ];
              for (const candidate of candidates) {
                const match = DEPARTAMENTOS_UY.find((d) => plano(d) === plano(candidate));
                if (match) { department = match; break; }
              }
              // Sin coincidencia no se pisa el select: createBuilding solo
              // acepta uno de los 19 departamentos.
            }
            if (component.types.includes("country")) {
              // El autocompletado está restringido a "uy"; el nombre puede venir
              // en el idioma del navegador ("Uruguai") y el servidor pide "Uruguay".
              country = component.short_name === "UY" ? "Uruguay" : component.long_name;
            }
            if (component.types.includes("route")) {
              streetAddress = component.long_name;
            }
            if (component.types.includes("street_number")) {
              streetAddress += " " + component.long_name;
            }
          });

          setFormData(prev => ({
            ...prev,
            address: place.formatted_address || streetAddress,
            city: city || prev.city,
            department: department || prev.department,
            country: country || prev.country,
            lat,
            lng
          }));
        }
      });

      autocompleteRef.current = autocomplete;
    }

    return () => {
      if (autocompleteRef.current) {
        window.google?.maps?.event?.clearInstanceListeners(autocompleteRef.current);
        autocompleteRef.current = null;
      }
    };
  }, [showModal, isLoaded]);

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value
    }));
    if (formErrors[name]) setFormErrors(prev => ({ ...prev, [name]: undefined }));
  };

  const cerrarModal = () => {
    if (saving) return; // el alta sigue en el servidor: esperar la respuesta
    setShowModal(false);
    setFormErrors({});
  };

  const handleCreateBuilding = async (e) => {
    e.preventDefault();
    if (saving) return;
    const errors = {};
    if (!formData.name.trim()) errors.name = "Requerido";
    if (!formData.address.trim()) errors.address = "Requerido";
    if (!formData.city.trim()) errors.city = "Requerido";
    if (!formData.department.trim()) errors.department = "Requerido";
    if (!formData.country.trim()) errors.country = "Requerido";
    const faltanObligatorios = Object.keys(errors).length > 0;
    const floors = String(formData.floors ?? "").trim();
    const elevators = String(formData.elevators ?? "").trim();
    if (floors && (!ENTERO.test(floors) || Number(floors) > MAX_PISOS)) {
      errors.floors = `Número entero entre 0 y ${MAX_PISOS}`;
    }
    if (elevators && (!ENTERO.test(elevators) || Number(elevators) > MAX_ASCENSORES)) {
      errors.elevators = `Número entero entre 0 y ${MAX_ASCENSORES}`;
    }
    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      showToast(faltanObligatorios ? "Completá todos los campos obligatorios (*)" : "Revisá los campos marcados", "warning");
      return;
    }
    setFormErrors({});

    // Las dos coordenadas o ninguna (las pone el autocompletado de Google).
    const conCoordenadas = Number.isFinite(formData.lat) && Number.isFinite(formData.lng);
    const datos = {
      name: formData.name.trim(),
      address: formData.address.trim(),
      city: formData.city.trim(),
      department: formData.department.trim(),
      country: formData.country.trim(),
      padron: (formData.padron || "").trim(),
      floors: floors === "" ? null : Number(floors),
      elevators: elevators === "" ? null : Number(elevators),
      complex: !!formData.complex,
      lat: conCoordenadas ? formData.lat : null,
      lng: conCoordenadas ? formData.lng : null,
    };
    const firma = JSON.stringify(datos);
    if (intentoRef.current.firma !== firma) intentoRef.current = { firma, requestId: nuevoRequestId() };

    setSaving(true);
    try {
      // El servidor asigna el buildingid (counters/buildingid), crea el edificio
      // y el link admin con la cuenta de administrador del que lo crea.
      const res = await createBuilding({ ...datos, requestId: intentoRef.current.requestId });
      intentoRef.current = { firma: null, requestId: null };

      // repetido: un intento anterior con el mismo requestId ya lo había creado.
      showToast(res?.repetido ? "El edificio ya se había creado." : "Edificio creado exitosamente", "success");
      if (res && res.adminUserid == null) {
        // Solo pasa con un superadmin sin cuenta de administrador propia.
        showToast("El edificio quedó sin administrador asignado. Asignalo desde Editar edificio → Administradores.", "warning");
      } else if (res && res.accesoActualizado === false) {
        // El recálculo de permisos no llegó a terminar en el alta: se pide de
        // nuevo sin esperar (los triggers también lo hacen).
        refreshMyAccess().catch(() => {});
        showToast("Tus permisos sobre el edificio nuevo pueden tardar unos segundos en actualizarse. Si no podés editarlo, esperá un momento.", "info");
      }
      setShowModal(false);
      setFormData(FORM_INICIAL);
      refreshBuildings();
    } catch (error) {
      console.error("Error creando edificio:", error);
      // El requestId se conserva para el mismo pedido: el reintento devuelve el
      // alta si ya se hizo (repetido) o la hace una sola vez.
      if (codigoDeError(error) === "aborted") {
        // Otro intento con el mismo requestId sigue en curso: no es un fallo.
        showToast(mensajeLegible(error, "El alta ya está en curso. Esperá unos segundos y volvé a tocar Crear."), "info");
      } else {
        showToast(mensajeLegible(error, "Error al crear el edificio"), "error");
      }
    } finally {
      setSaving(false);
    }
  };

  // Filtrar edificios
  const filteredBuildings = buildings.filter(b => {
    const term = searchTerm.toLowerCase();
    return (
      (b.name || "").toLowerCase().includes(term) ||
      (b.address || "").toLowerCase().includes(term) ||
      (b.city || "").toLowerCase().includes(term)
    );
  });

  // Edificios con coordenadas para el mapa
  const buildingsWithCoords = filteredBuildings.filter(b => b.latitude && b.longitude);

  // Centrar mapa en edificios
  const getMapCenter = () => {
    if (buildingsWithCoords.length > 0) {
      const avgLat = buildingsWithCoords.reduce((sum, b) => sum + b.latitude, 0) / buildingsWithCoords.length;
      const avgLng = buildingsWithCoords.reduce((sum, b) => sum + b.longitude, 0) / buildingsWithCoords.length;
      return { lat: avgLat, lng: avgLng };
    }
    return defaultCenter;
  };

  const handleBuildingClick = (building) => {
    setSelectedBuilding({
      id: building.id,
      buildingid: building.buildingid,
      name: building.name,
      address: building.address,
      city: building.city,
      department: building.department,
      country: building.country,
    });
    setSelected("editaredificio");
  };

  if (loading) return <Loader />;

  return (
    <div style={styles.page}>
      {/* Header con título y acciones */}
      <div style={styles.pageHeader}>
        <ScreenHeader icon={FaBuilding} title="Mis Edificios" subtitle="Gestión de edificios" />
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <div style={styles.viewToggle} data-tutorial="edificios-toggle">
            <button
              style={{
                ...styles.viewBtn,
                ...(viewMode === "list" ? styles.viewBtnActive : {})
              }}
              onClick={() => setViewMode("list")}
            >
              <FaList size={14} /> Lista
            </button>
            <button
              style={{
                ...styles.viewBtn,
                ...(viewMode === "map" ? styles.viewBtnActive : {})
              }}
              onClick={() => setViewMode("map")}
            >
              <FaMap size={14} /> Mapa
            </button>
          </div>
          <button
            style={styles.addBtn}
            onClick={() => setShowModal(true)}
            data-tutorial="edificios-nuevo"
          >
            <FaPlusCircle size={16} /> Nuevo Edificio
          </button>
        </div>
      </div>

      {/* Vista de Mapa */}
      {viewMode === "map" && (
        <div style={styles.mapContainer}>
          {!GOOGLE_MAPS_API_KEY ? (
            <div style={styles.mapPlaceholder}>
              <FaMapPin size={48} style={{ color: colors.gray300, marginBottom: spacing[4] }} />
              <p style={{ color: colors.gray600, margin: 0 }}>
                Configura tu API Key de Google Maps en el archivo .env.local
              </p>
              <code style={styles.codeBlock}>
                REACT_APP_GOOGLE_MAPS_API_KEY=tu_api_key
              </code>
            </div>
          ) : loadError ? (
            <div style={styles.mapPlaceholder}>
              <p style={{ color: colors.error }}>Error cargando Google Maps</p>
            </div>
          ) : !isLoaded ? (
            <div style={styles.mapPlaceholder}>
              <Loader />
            </div>
          ) : (
            <GoogleMap
              mapContainerStyle={mapContainerStyle}
              center={getMapCenter()}
              zoom={buildingsWithCoords.length > 0 ? 13 : 12}
              onLoad={onMapLoad}
              options={{
                styles: mapStyles,
                disableDefaultUI: false,
                zoomControl: true,
                mapTypeControl: false,
                streetViewControl: false,
                fullscreenControl: true,
              }}
            >
              {buildingsWithCoords.map((b) => (
                <Marker
                  key={b.id}
                  position={{ lat: b.latitude, lng: b.longitude }}
                  onClick={() => setSelectedMarker(b)}
                  icon={{
                    url: "data:image/svg+xml," + encodeURIComponent(`
                      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="${colors.primary}" width="36" height="36">
                        <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z"/>
                      </svg>
                    `),
                    scaledSize: new window.google.maps.Size(36, 36),
                  }}
                />
              ))}
              {selectedMarker && (
                <InfoWindow
                  position={{ lat: selectedMarker.lat, lng: selectedMarker.lng }}
                  onCloseClick={() => setSelectedMarker(null)}
                >
                  <div style={styles.infoWindow}>
                    <h3 style={styles.infoTitle}>{selectedMarker.name}</h3>
                    <p style={styles.infoAddress}>{selectedMarker.address}</p>
                    <p style={styles.infoCity}>
                      {selectedMarker.city}, {selectedMarker.department}
                    </p>
                    <button
                      style={styles.infoBtn}
                      onClick={() => handleBuildingClick(selectedMarker)}
                    >
                      Ver detalles
                    </button>
                  </div>
                </InfoWindow>
              )}
            </GoogleMap>
          )}
          {buildingsWithCoords.length === 0 && buildings.length > 0 && GOOGLE_MAPS_API_KEY && isLoaded && (
            <p style={styles.noCoords}>
              Los edificios existentes no tienen coordenadas. Edítalos para agregar ubicación.
            </p>
          )}
        </div>
      )}

      {/* Vista de Lista */}
      {viewMode === "list" && (
        <>
          {filteredBuildings.length === 0 ? (
            <div style={styles.emptyState}>
              <FaBuilding size={48} style={{ color: colors.gray300, marginBottom: spacing[4] }} />
              <p style={{ color: colors.gray600, fontSize: typography.sizes.lg }}>
                {searchTerm ? "No se encontraron edificios" : "No tenés edificios cargados."}
              </p>
              {!searchTerm && (
                <button
                  style={styles.addBtnEmpty}
                  onClick={() => setShowModal(true)}
                >
                  <FaPlusCircle size={16} /> Agregar primer edificio
                </button>
              )}
            </div>
          ) : (
            <div style={styles.grid} data-tutorial="edificios-lista">
              {filteredBuildings.map((b) => {
                const badgeBaja = BADGE_BAJA[pendingDeletions.get(b.id)];
                const isPendingDeletion = !!badgeBaja;
                return (
                <div
                  key={b.id}
                  style={{
                    ...styles.card,
                    ...(isPendingDeletion ? {
                      opacity: 0.55,
                      borderColor: colors.warning,
                    } : {}),
                  }}
                  onClick={() => handleBuildingClick(b)}
                  onMouseOver={(e) => (e.currentTarget.style.transform = "translateY(-4px)")}
                  onMouseOut={(e) => (e.currentTarget.style.transform = "translateY(0)")}
                >
                  <div style={styles.cardHeader}>
                    <FaBuilding size={18} />
                    <h3 style={styles.cardTitle} title={b.name || "Sin nombre"}>{b.name || "Sin nombre"}</h3>
                  </div>
                  {isPendingDeletion && (
                    <div style={{ marginBottom: 4 }}>
                      <StatusBadge status={badgeBaja.status} label={badgeBaja.label} size="sm" />
                    </div>
                  )}

                  <div style={styles.row}>
                    <FaMapMarkerAlt size={14} style={{ opacity: 0.9 }} />
                    <span style={styles.cardText}>
                      {b.address || "—"} — {b.city || "—"}
                    </span>
                  </div>

                  <div style={styles.cardFooter}>
                    <span style={b.complex ? styles.badgeComplex : styles.badgeSimple}>
                      {b.complex ? "Complejo" : "Edificio"}
                    </span>
                    {b.latitude && b.longitude ? (
                      <button
                        style={styles.locationBtnGreen}
                        onClick={(e) => {
                          e.stopPropagation();
                          setViewMode("map");
                          setSelectedMarker(b);
                          // Centrar el mapa en este edificio
                          setTimeout(() => {
                            if (mapRef.current) {
                              mapRef.current.panTo({ lat: b.latitude, lng: b.longitude });
                              mapRef.current.setZoom(16);
                            }
                          }, 300);
                        }}
                      >
                        <FaMapPin size={10} /> Ver ubicación
                      </button>
                    ) : (
                      <button
                        style={styles.locationBtnOrange}
                        onClick={(e) => {
                          e.stopPropagation();
                          handleBuildingClick(b);
                        }}
                      >
                        <FaMapPin size={10} /> Agregar ubicación
                      </button>
                    )}
                  </div>
                </div>
                );
              })}
            </div>
          )}
        </>
      )}

      {/* Modal Crear Edificio */}
      <AdminModal
        open={showModal}
        onClose={cerrarModal}
        title="Nuevo Edificio"
        icon={<FaBuilding />}
        size="medium"
        hideClose
        footer={
          <>
            <button
              type="button"
              style={{ ...styles.cancelButton, ...(saving ? styles.buttonDisabled : {}) }}
              disabled={saving}
              onClick={cerrarModal}
              className="habitia-btn-secondary"
            >
              Cancelar
            </button>
            <button
              type="button"
              style={{ ...styles.submitButton, ...(saving ? styles.buttonDisabled : {}) }}
              disabled={saving}
              onClick={handleCreateBuilding}
              className="habitia-btn-primary"
              data-tutorial="edificios-btn-crear"
            >
              {saving ? "Creando..." : "Crear"}
            </button>
          </>
        }
      >
        <div style={styles.form} data-tutorial="edificios-form">
          {/* Nombre y Dirección en una fila */}
          <div style={styles.formRow}>
            <FormField label="Nombre" htmlFor="edificio-nuevo-nombre" required error={formErrors.name} style={{ flex: 1 }} data-tutorial="edificios-campo-nombre">
              <input
                id="edificio-nuevo-nombre"
                type="text"
                name="name"
                className="habitia-input"
                value={formData.name}
                onChange={handleInputChange}
                placeholder="Torre Central"
                maxLength={120}
                style={{ ...styles.input, ...(formErrors.name ? { borderColor: colors.error } : {}) }}
              />
            </FormField>
            <FormField label="Dirección" htmlFor="edificio-nuevo-direccion" required error={formErrors.address} style={{ flex: 1.5 }} data-tutorial="edificios-campo-direccion">
              <input
                id="edificio-nuevo-direccion"
                ref={inputRef}
                type="text"
                name="address"
                className="habitia-input"
                value={formData.address}
                onChange={handleInputChange}
                placeholder="Buscar dirección..."
                maxLength={200}
                style={{ ...styles.input, ...(formErrors.address ? { borderColor: colors.error } : {}) }}
              />
            </FormField>
          </div>

          {/* Ciudad, Departamento, País */}
          <div style={styles.formRow} data-tutorial="edificios-campos-auto">
            <FormField label="Ciudad" htmlFor="edificio-nuevo-ciudad" required error={formErrors.city}>
              <input
                id="edificio-nuevo-ciudad"
                type="text"
                name="city"
                className="habitia-input"
                value={formData.city}
                onChange={handleInputChange}
                placeholder="Montevideo"
                maxLength={80}
                style={{ ...styles.input, ...(formErrors.city ? { borderColor: colors.error } : {}) }}
              />
            </FormField>
            <FormField label="Departamento" htmlFor="edificio-nuevo-departamento" required error={formErrors.department}>
              <select
                id="edificio-nuevo-departamento"
                name="department"
                className="habitia-input"
                value={formData.department}
                onChange={handleInputChange}
                style={{ ...styles.input, ...(formErrors.department ? { borderColor: colors.error } : {}) }}
              >
                <option value="">Seleccionar...</option>
                {DEPARTAMENTOS_UY.map((dep) => (
                  <option key={dep} value={dep}>{dep}</option>
                ))}
              </select>
            </FormField>
            <FormField label="País" htmlFor="edificio-nuevo-pais" required error={formErrors.country}>
              <select
                id="edificio-nuevo-pais"
                name="country"
                className="habitia-input"
                value={formData.country}
                onChange={handleInputChange}
                style={{ ...styles.input, ...(formErrors.country ? { borderColor: colors.error } : {}) }}
              >
                <option value="Uruguay">Uruguay</option>
              </select>
            </FormField>
          </div>

          {/* Padrón */}
          <div style={styles.formRow} data-tutorial="edificios-campo-padron">
            <FormField label="Padrón" htmlFor="edificio-nuevo-padron">
              <input
                id="edificio-nuevo-padron"
                type="text"
                name="padron"
                className="habitia-input"
                value={formData.padron}
                onChange={handleInputChange}
                placeholder="Número de padrón"
                maxLength={30}
                style={styles.input}
              />
            </FormField>
          </div>

          {/* Pisos y Ascensores */}
          <div style={styles.formRow}>
            <FormField label="Pisos" htmlFor="edificio-nuevo-pisos" error={formErrors.floors}>
              <input
                id="edificio-nuevo-pisos"
                type="number"
                name="floors"
                className="habitia-input"
                min="0"
                max={MAX_PISOS}
                step="1"
                value={formData.floors}
                onChange={handleInputChange}
                placeholder="Cantidad de pisos"
                style={{ ...styles.input, ...(formErrors.floors ? { borderColor: colors.error } : {}) }}
              />
            </FormField>
            <FormField label="Ascensores" htmlFor="edificio-nuevo-ascensores" error={formErrors.elevators}>
              <input
                id="edificio-nuevo-ascensores"
                type="number"
                name="elevators"
                className="habitia-input"
                min="0"
                max={MAX_ASCENSORES}
                step="1"
                value={formData.elevators}
                onChange={handleInputChange}
                placeholder="Cantidad de ascensores"
                style={{ ...styles.input, ...(formErrors.elevators ? { borderColor: colors.error } : {}) }}
              />
            </FormField>
          </div>

          {/* Complejo */}
          <div style={styles.formRow} data-tutorial="edificios-campo-complejo">
            <div style={{ ...styles.formGroup, justifyContent: "flex-end" }}>
              <label style={styles.checkboxLabel}>
                <input
                  type="checkbox"
                  name="complex"
                  checked={formData.complex}
                  onChange={handleInputChange}
                  style={styles.checkbox}
                />
                Es complejo
              </label>
            </div>
          </div>

          {/* Coordenadas (solo lectura) */}
          {formData.lat && formData.lng && (
            <div style={styles.coordsInfo}>
              <FaMapPin size={14} style={{ color: colors.primary }} />
              <span>Ubicación: {formData.lat.toFixed(6)}, {formData.lng.toFixed(6)}</span>
            </div>
          )}
        </div>
      </AdminModal>
    </div>
  );
};

// Estilos del mapa (minimalista)
const mapStyles = [
  {
    featureType: "poi",
    elementType: "labels",
    stylers: [{ visibility: "off" }]
  },
  {
    featureType: "transit",
    elementType: "labels",
    stylers: [{ visibility: "off" }]
  }
];

const styles = {
  // Contenedor principal de la página
  page: {
    padding: spacing[6],
    background: colors.bgMain,
    minHeight: "100vh",
    fontFamily: typography.fontFamily,
  },
  // Cabecera con título y botón de acción
  pageHeader: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    marginBottom: spacing[3],
  },
  headerTitleSection: {
    display: "flex",
    flexDirection: "column",
    gap: 2,
  },
  // Título de página (usa token pageTitle)
  pageTitle: { ...pageTitle },
  pageSubtitle: {
    fontSize: typography.sizes.base,
    color: colors.gray600,
    margin: 0,
  },
  // Botón volver
  backBtn: {
    ...btnSecondary,
    boxShadow: shadows.xs,
  },
  // Título genérico
  title: {
    fontSize: typography.sizes["2xl"],
    fontWeight: 400,
    margin: 0,
    flex: 1,
    color: colors.black,
  },
  // Botón principal "Nuevo Edificio"
  addBtn: {
    ...btnPrimary,
  },
  // Barra de herramientas: búsqueda + vista
  toolbar: {
    display: "flex",
    gap: spacing[4],
    marginBottom: spacing[5],
    flexWrap: "wrap",
    alignItems: "center",
  },
  // Caja de búsqueda
  searchBox: {
    display: "flex",
    alignItems: "center",
    gap: spacing[3],
    background: colors.bgCard,
    borderRadius: radius.sm,
    padding: "10px 14px",
    flex: 1,
    minWidth: 250,
    boxShadow: shadows.xs,
  },
  searchInput: {
    border: "none",
    outline: "none",
    fontSize: typography.sizes.md,
    flex: 1,
    background: "transparent",
    fontFamily: typography.fontFamily,
  },
  // Toggle lista / mapa
  viewToggle: {
    display: "flex",
    gap: spacing[1],
    background: colors.gray100,
    borderRadius: radius.sm,
    padding: spacing[1],
    boxShadow: shadows.xs,
  },
  viewBtn: {
    ...btnGhost,
    gap: spacing[2],
    padding: `${spacing[2]}px ${spacing[3]}px`,
    borderRadius: radius.sm - 2,
    fontSize: typography.sizes.base,
    fontWeight: 400,
    color: colors.gray600,
  },
  viewBtnActive: {
    background: colors.primary,
    color: colors.white,
    boxShadow: shadows.sm,
  },
  // Contenedor del mapa
  mapContainer: {
    marginBottom: spacing[5],
  },
  // Placeholder cuando no hay mapa
  mapPlaceholder: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    height: 400,
    background: colors.bgCard,
    borderRadius: radius.md,
    boxShadow: shadows.sm,
  },
  // Bloque de código (instrucciones API key)
  codeBlock: {
    marginTop: spacing[3],
    padding: `${spacing[2]}px ${spacing[3]}px`,
    background: colors.gray100,
    borderRadius: radius.sm - 2,
    fontSize: typography.sizes.sm,
    color: colors.gray900,
  },
  // Texto sin coordenadas
  noCoords: {
    textAlign: "center",
    color: colors.gray600,
    fontSize: typography.sizes.md,
    marginTop: spacing[3],
  },
  // InfoWindow del mapa
  infoWindow: {
    padding: spacing[1],
    minWidth: 180,
  },
  infoTitle: {
    margin: "0 0 4px",
    fontSize: 15,
    fontWeight: 400,
    color: colors.black,
  },
  infoAddress: {
    margin: "0 0 2px",
    fontSize: typography.sizes.base,
    color: colors.gray700,
  },
  infoCity: {
    margin: `0 0 ${spacing[2]}px`,
    fontSize: typography.sizes.sm,
    color: colors.gray600,
  },
  // Botón dentro del InfoWindow
  infoBtn: {
    ...btnPrimary,
    fontSize: typography.sizes.sm,
    padding: `${spacing[2]}px ${spacing[3]}px`,
    width: "100%",
    justifyContent: "center",
  },
  // Estado vacío (sin edificios)
  emptyState: {
    ...cardBase,
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    padding: 60,
  },
  // Botón agregar en estado vacío
  addBtnEmpty: {
    ...btnPrimary,
    marginTop: spacing[4],
  },
  // Grilla de tarjetas
  grid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))",
    gap: spacing[4],
  },
  // Tarjeta de edificio (sin borde, solo sombra)
  card: {
    ...cardBase,
    cursor: "pointer",
    display: "flex",
    flexDirection: "column",
    gap: spacing[2],
  },
  // Cabecera de la tarjeta
  cardHeader: {
    display: "flex",
    alignItems: "center",
    gap: spacing[3],
    marginBottom: spacing[1],
    color: colors.primary,
  },
  // Título de la tarjeta
  cardTitle: {
    margin: 0,
    fontSize: typography.sizes.lg,
    fontWeight: 400,
    color: colors.black,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  // Fila de datos
  row: {
    display: "flex",
    alignItems: "center",
    gap: spacing[2],
    color: colors.gray600,
  },
  cardText: {
    margin: 0,
    fontSize: typography.sizes.base,
  },
  // Pie de la tarjeta
  cardFooter: {
    display: "flex",
    gap: spacing[2],
    marginTop: spacing[2],
  },
  // Badge tipo "Complejo"
  badgeComplex: {
    display: "inline-flex",
    alignItems: "center",
    padding: `${spacing[1]}px ${spacing[2]}px`,
    borderRadius: radius.sm - 2,
    background: colors.infoLight,
    color: colors.infoDark,
    fontSize: typography.sizes.xs,
    fontWeight: 400,
  },
  // Badge tipo "Edificio"
  badgeSimple: {
    display: "inline-flex",
    alignItems: "center",
    padding: `${spacing[1]}px ${spacing[2]}px`,
    borderRadius: radius.sm - 2,
    background: colors.gray100,
    color: colors.gray700,
    fontSize: typography.sizes.xs,
    fontWeight: 400,
  },
  // Badge de ubicación
  badgeLocation: {
    display: "inline-flex",
    alignItems: "center",
    gap: spacing[1],
    padding: `${spacing[1]}px ${spacing[2]}px`,
    borderRadius: radius.sm - 2,
    background: colors.successLight,
    color: colors.success,
    fontSize: typography.sizes.xs,
    fontWeight: 400,
  },
  // Botón verde "Ver ubicación"
  locationBtnGreen: {
    ...btnGhost,
    gap: spacing[1],
    padding: `${spacing[1]}px ${spacing[3]}px`,
    borderRadius: radius.sm - 2,
    background: colors.successLight,
    color: colors.success,
    fontSize: typography.sizes.xs,
    fontWeight: 400,
  },
  // Botón naranja "Agregar ubicación"
  locationBtnOrange: {
    ...btnGhost,
    gap: spacing[1],
    padding: `${spacing[1]}px ${spacing[3]}px`,
    borderRadius: radius.sm - 2,
    background: colors.warningLight,
    color: colors.error,
    fontSize: typography.sizes.xs,
    fontWeight: 400,
  },
  // Formulario dentro del modal (padding manejado por AdminModal)
  form: {},
  formRow: {
    display: "flex",
    gap: spacing[3],
    marginBottom: spacing[3],
  },
  formGroup: {
    display: "flex",
    flexDirection: "column",
    gap: spacing[1],
    flex: 1,
  },
  // Label de campo
  label: {
    fontSize: typography.sizes.sm,
    fontWeight: 400,
    color: colors.gray900,
    display: "flex",
    alignItems: "center",
  },
  // Input de formulario (usa inputBase)
  input: {
    ...inputBase,
    backgroundColor: colors.gray50,
  },
  // Label de checkbox
  checkboxLabel: {
    display: "flex",
    alignItems: "center",
    gap: spacing[2],
    fontSize: typography.sizes.base,
    color: colors.gray900,
    cursor: "pointer",
    padding: "10px 0",
  },
  checkbox: {
    width: 16,
    height: 16,
    cursor: "pointer",
    accentColor: colors.primary,
  },
  // Info de coordenadas
  coordsInfo: {
    display: "flex",
    alignItems: "center",
    gap: spacing[2],
    padding: `${spacing[2]}px ${spacing[3]}px`,
    background: colors.primaryLighter,
    borderRadius: radius.sm,
    fontSize: typography.sizes.sm,
    color: colors.primaryDark,
    marginBottom: spacing[3],
  },
  // Error de campo
  fieldError: {
    fontSize: typography.sizes.xs,
    color: colors.error,
    marginTop: 3,
    display: "block",
  },
  // Botón cancelar del modal de creación
  cancelButton: {
    ...btnSecondary,
    fontFamily: "inherit",
  },
  // Botón submit del modal de creación
  submitButton: {
    ...btnPrimary,
    fontFamily: "inherit",
  },
  // Botones del modal mientras se crea el edificio
  buttonDisabled: {
    opacity: 0.6,
    cursor: "not-allowed",
  },
};

export default MisEdificios;
